import AddToCart from "components/item/AddToCart";
import { Box, Button, FormattedCurrency, HStack, Icon, VStack } from "components/ui";
import { H1, Paragraph, Typography } from "components/ui/Typography";
import { Trash2 } from "lucide-react-native";
import React, { useState, useCallback } from "react";
import Animated from "react-native-reanimated";
import { Item, usePutCartCurrentItemMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useSiteSearch } from "utils/hooks/useSiteSearch";
import { useUserPermission } from "utils/hooks/useUserPermission";
import { ItemAlternativeButton } from "./ItemAlternativeButton";
import { ItemListItemBaseProps, renderItemContent, renderItemImage } from "./ItemListItemBase";
import ItemSlideSheet from "./ItemSlideSheet";
import RechargeableField from "./RechargeableField";
import ItemStatusBadge from "./ItemStatusBadge";
import { reportAddSearchClick } from "utils/addSearchAnalytics";

type Props = ItemListItemBaseProps & {
	variant?: "standard" | "checkout" | "stocklist" | "order";
	removeItem?: (stockcode: string) => void;
	showAlternatives?: boolean;
	showRechargeable?: boolean;
	onRechargeableChange?: (stockcode: string, newValue: boolean, quantity?: number) => void;
	location?: number | null;
};

const ItemListItem = React.memo(function ItemListItem({
	itemWithQuantity,
	item,
	variant = "standard",
	removeItem,
	showAlternatives = true,
	isOnNavigation,
	showRechargeable,
	onRechargeableChange,
	location = null,
	isOnSearch = false,
}: Props) {
	const isMobile = useIsMobile();
	const [updateItem, updateStatus] = usePutCartCurrentItemMutation();
	const [isSheetOpen, setSheetOpen] = useState(false);
	const [isFading, setIsFading] = useState(false);
	const { close } = useSiteSearch();

	const { canApplyRechargeable } = useUserPermission();

	if (itemWithQuantity == null && item == null) {
		return null;
	}

	let itemInfo = item as Item;
	if (itemWithQuantity != null && itemWithQuantity.item) {
		itemInfo = itemWithQuantity.item as Item;
	}

	// Memoize callback functions to prevent recreation
	const toggleSheet = useCallback(() => setSheetOpen(!isSheetOpen), [isSheetOpen]);

	const removeItemHandler = useCallback(() => {
		if (removeItem && itemInfo?.stockcode) {
			removeItem(itemInfo.stockcode);
		}
	}, [removeItem, itemInfo?.stockcode]);

	const { searchTerm } = useSiteSearch();

	const handleClick = useCallback(() => {
		if (isOnSearch && searchTerm?.length > 0 && itemInfo?.slug) {
			reportAddSearchClick({
				keyword: searchTerm,
				docid: itemInfo.slug,
			});
		}
		if (isOnNavigation) {
			close();
		}
	}, [isOnSearch, searchTerm, itemInfo?.slug, isOnNavigation, close]);

	const renderRechargeableField = () => {
		if (!showRechargeable || !canApplyRechargeable) return null;

		return <RechargeableField isChecked={itemInfo?.rechargeable ?? false} onChange={(val) => onRechargeableChange?.(itemInfo?.stockcode ?? "", val, itemWithQuantity?.quantity)} />;
	};

	if (isMobile) {
		let isSpecial = false;

		if (location) {
			const itemAvailability = itemInfo.itemAvailability?.find((ia) => ia.locationId === location);
			if (itemAvailability?.status === "OutOfStock" && itemAvailability?.classification === "Special Order Only") {
				isSpecial = true;
			}
		}
		return (
			<Box paddingHorizontal={12}>
				<HStack w={"$full"} paddingVertical={20} gap={12} borderBottomWidth={1} borderBottomColor="$neutral100" alignItems="flex-start">
					{/* {renderItemImage(itemInfo)} */}

					<Box flex={3} gap={6} justifyContent="flex-start" minWidth={0}>
						{isSpecial && (
							<Box alignSelf="flex-start" bg="$darkteal" borderRadius={6} paddingHorizontal={8} mb={8}>
								<Typography.Paragraph sx={{ fontSize: 11, color: "$basicwhite", textTransform: "uppercase", letterSpacing: 6, textAlign: "center", marginLeft: 6 }} weight="bold">
									SPECIAL
								</Typography.Paragraph>
							</Box>
						)}

						{renderItemContent(itemInfo, variant, location, handleClick)}
						{variant === "stocklist" && (
							<Box>
								<FormattedCurrency
									sx={{
										flex: 1,
										fontSize: "$h4mobile",
										fontWeight: "bold",
										color: "$basicblack",
									}}
									amount={itemInfo.unitPrice}
								/>
							</Box>
						)}
						{variant === "stocklist" && renderRechargeableField()}
						{showAlternatives && <ItemAlternativeButton isStockList={variant === "stocklist"} item={itemInfo} />}
					</Box>

					<VStack flex={2} gap={8} alignItems="flex-end" justifyContent="flex-start" minWidth={100}>
						{variant === "order" && (
							<Box alignItems="center" justifyContent="center" paddingTop={4}>
								<Paragraph>Qty: {itemWithQuantity?.quantity}</Paragraph>
							</Box>
						)}
						{variant === "stocklist" && (
							<Box paddingTop={4}>
								<Button sx={{ padding: 8 }} onPress={removeItemHandler}>
									<Icon w={20} h={20} as={Trash2} color="$basicblack" />
								</Button>
							</Box>
						)}

						{variant === "standard" && itemInfo.isPurchaseable && (
							<Box width="100%" maxWidth={160}>
								<AddToCart stockcode={itemInfo.stockcode} rechargeable={itemInfo.rechargeable} isPurchaseable={itemInfo.isPurchaseable} item={itemInfo} isOnSearch={isOnSearch} />
							</Box>
						)}
						{variant === "checkout" && itemInfo.isPurchaseable && (
							<VStack gap={8} width="100%" alignItems="flex-end">
								<Box flex={1}>
									<AddToCart stockcode={itemInfo.stockcode} rechargeable={itemInfo.rechargeable} isPurchaseable={itemInfo.isPurchaseable} item={itemInfo} isOnSearch={isOnSearch} />
								</Box>
								{renderRechargeableField()}
								<FormattedCurrency
									sx={{
										fontSize: "$h4mobile",
										fontWeight: "bold",
										color: "$basicblack",
										textAlign: "right",
									}}
									amount={itemInfo.unitPrice}
								/>
							</VStack>
						)}
						{variant === "order" && (
							<VStack gap={4} alignItems="flex-end">
								<FormattedCurrency
									sx={{
										fontSize: "$h4mobile",
										fontWeight: "bold",
										color: "$basicblack",
										textAlign: "right",
									}}
									amount={itemInfo.unitPrice}
								/>
							</VStack>
						)}
					</VStack>
				</HStack>
				<ItemSlideSheet item={itemInfo} isSheetOpen={isSheetOpen} toggleSheet={toggleSheet} />
			</Box>
		);
	}

	if (itemInfo == null) {
		return <Box>No data found for </Box>;
	}

	return (
		<Animated.View
			style={{
				paddingHorizontal: 20,
			}}
		>
			<HStack w={"$full"} paddingVertical={28} gap={20} borderBottomWidth={1} borderBottomColor="$neutral100">
				{renderItemImage(itemInfo, handleClick)}

				<Box flex={2} justifyContent="center">
					{renderItemContent(itemInfo, variant, location === null ? null : location, handleClick)}
					{variant === "stocklist" && (
						<Box>
							<FormattedCurrency
								sx={{
									flex: 1,
									fontSize: "$h4desktop",
									fontWeight: "bold",
									color: "$basicblack",
								}}
								amount={itemInfo.unitPrice}
							/>
						</Box>
					)}
					{variant === "stocklist" && renderRechargeableField()}
					{showAlternatives && <ItemAlternativeButton isStockList item={itemInfo} />}
				</Box>

				<HStack flex={1} gap={12} marginLeft={"auto"}>
					{variant === "order" && (
						<Box>
							<Paragraph>{itemWithQuantity?.quantity}</Paragraph>
						</Box>
					)}
					{variant === "stocklist" && (
						<Box>
							<Button sx={{ padding: 0 }} onPress={removeItemHandler}>
								<Icon flex={1} w={24} h={24} as={Trash2} color="$basicblack" />
							</Button>
						</Box>
					)}

					{variant === "standard" && itemInfo.isPurchaseable && (
						<Box marginLeft={"auto"}>
							<AddToCart stockcode={itemInfo.stockcode} rechargeable={itemInfo.rechargeable} isPurchaseable={itemInfo.isPurchaseable} item={itemInfo} isOnSearch={isOnSearch} />
						</Box>
					)}
					{variant === "checkout" && itemInfo.isPurchaseable && (
						<Box marginLeft={"auto"}>
							<VStack gap={4} h="$full">
								<AddToCart stockcode={itemInfo.stockcode} rechargeable={itemInfo.rechargeable} isPurchaseable={itemInfo.isPurchaseable} item={itemInfo} isOnSearch={isOnSearch} />
								{renderRechargeableField()}
								<HStack marginTop={"auto"} gap={8}>
									<FormattedCurrency
										sx={{
											flex: 1,
											marginLeft: "auto",
											fontSize: "$h4desktop",
											fontWeight: "bold",
											color: "$basicblack",
										}}
										amount={itemInfo.unitPrice}
									/>
								</HStack>
							</VStack>
						</Box>
					)}
					{variant === "order" && (
						<Box marginLeft={"auto"}>
							<VStack gap={4} h="$full">
								<HStack marginTop={"auto"} gap={8}>
									<FormattedCurrency
										sx={{
											flex: 1,
											marginLeft: "auto",
											fontSize: "$h4desktop",
											fontWeight: "bold",
											color: "$basicblack",
										}}
										amount={itemInfo.unitPrice}
									/>
								</HStack>
							</VStack>
						</Box>
					)}
				</HStack>
			</HStack>
			<ItemSlideSheet item={itemInfo} isSheetOpen={isSheetOpen} toggleSheet={toggleSheet} />
		</Animated.View>
	);
});

export default ItemListItem;
